import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger
} from '~/components/UI/shadcn/drawer'

import PlayButton from '~/components/UI/Buttons/PlayButton/PlayButton'
import GeneralAccordion from '~/components/Content/Accordion/GeneralAccordion'
import Image from '~/components/Helpers/Image'
import { useQuery } from '@tanstack/react-query'
import { getEntryById } from '~/lib/contensis/misc'
import Loader from '~/components/UI/General/Loader/Loader'

interface VideoLauncherProps {
  content: {
    itemID: string
    sys: { id?: string }
    video: {
      heading: string
      copy: string
      duration: number
      placeholderImage: {
        asset: {
          sys: {
            uri: string
          }
        }
        altText: string
      }
      youtubeCode: string
      transcript: string
      sys: { id?: string }
    }
  }
}

const VideoLauncher = ({ content }: VideoLauncherProps) => {
  const videoId = content?.video?.sys?.id || content?.sys?.id
  // Fetch video data by itemID
  const {
    data: videoData,
    isLoading,
    error
  } = useQuery({
    queryKey: ['video-launcher', videoId],
    queryFn: async () => getEntryById(videoId || ''),
    enabled: !!videoId
  })

  const video = videoData || content?.video || content

  const { heading, copy, duration, placeholderImage, youtubeCode, transcript } =
    video

  if (isLoading)
    return (
      <div className='py-l flex w-full justify-center'>
        <Loader fill={'#f5b6dc'} />
      </div>
    )
  if (error) return <div>Error loading video.</div>
  return (
    <Drawer>
      <DrawerTrigger asChild>
        <div className='group aspect-3/2 max-w-[56.625rem] cursor-pointer'>
          <div className='bg-blue-l relative size-full'>
            <div className='absolute bottom-0 h-[180px] w-full bg-gradient-to-t from-black/70 to-black/0'></div>
            <Image
              image={placeholderImage}
              classes='h-full w-full object-cover'
            />

            <div className='p-s absolute bottom-0 z-10 flex w-full items-end justify-between'>
              <div className='text-white'>
                {heading && (
                  <p className='mb-3xs text-base-sm sm:text-base-md lg:text-base-lg'>
                    {heading}
                  </p>
                )}
                {duration && (
                  <p className='text-tag-sm -tracking-normal italic'>
                    {duration + (duration > 1 ? ' mins' : ' min')}
                  </p>
                )}
              </div>
              {video && (
                <div className='*:uppercase'>
                  <PlayButton
                    colour={'Chartreuse'}
                    data={{
                      duration: 0,
                      ariaLabel: '',
                      label: 'Watch'
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </DrawerTrigger>

      <DrawerContent className='p-l pt-s mx-smax-w-[1400px] sm:mx-auto'>
        <div className='space-y-m px-2xs mx-auto w-full max-w-[916px] overflow-y-auto'>
          <div className='h-2xs-plus bg-blue-d-20 mx-auto w-[75px] rounded-full sm:w-[100px] lg:w-[175px]'></div>
          <DrawerHeader className='space-y-xs'>
            {heading && (
              <DrawerTitle className='gap-m text-h2-reg-sm flex items-center justify-between'>
                {heading}
                {duration && (
                  <span className='text-base-sm shrink-0 font-normal'>
                    {duration + (duration > 1 ? ' mins' : ' min')}
                  </span>
                )}
              </DrawerTitle>
            )}
            {copy && (
              <DrawerDescription className='hidden md:block'>
                {copy}
              </DrawerDescription>
            )}
          </DrawerHeader>
          {video && (
            <div className='mx-auto aspect-video max-w-[90vh]'>
              <iframe
                width='560'
                height='315'
                src={'https://www.youtube.com/embed/' + youtubeCode}
                title='YouTube video player'
                allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
                referrerPolicy='strict-origin-when-cross-origin'
                allowFullScreen
                className='h-full w-full'
              ></iframe>
            </div>
          )}
          {transcript && (
            <DrawerFooter className='h-max'>
              <GeneralAccordion
                content={{
                  backgroundColour: 'default',
                  contentSingleAccordion: {
                    heading: 'Transcript',
                    copy: transcript
                  }
                }}
                transcript={true}
              />
            </DrawerFooter>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  )
}

export default VideoLauncher
